import { useQuery } from '@apollo/client';
import {
  AddCircle,
  AppRegistration,
  Category,
  CleaningServices,
  Difference,
  Edit,
  GetApp,
  MoreVert,
  Settings,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useCleanupTemplateVersions, useDeleteTemplate } from '~/api/templates';
import { AppCard } from '~/components/AppCard';
import {
  FilterAccordionContainer,
  FilterCollapsibleList,
  FilterContainer,
  FilterFooter,
  FilterHeader,
  FilterSidebar,
  FilterToggle,
  parseUrlParam,
} from '~/components/Filter';
import { GridContainer } from '~/components/GridContainer';
import { IconButton } from '~/components/IconButton';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { Tooltip } from '~/components/Tooltip';
import { Count } from '~/components/count';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { EmptyState } from '~/components/empty-state';
import { SearchBar } from '~/components/forms/search-bar';
import { ButtonLink } from '~/components/link';
import { MoreActionLink, MoreDeleteAction } from '~/components/table';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLocation } from '~/hooks/location';
import { useMoreActions } from '~/hooks/table';
import AddAppsIllustration from '~/images/illustrations/add-apps.svg';
import { assert } from '~/lib/assert';
import { isSmDown } from '~/lib/responsive';
import { AddAppDialog, useCreateTemplateDialog } from '../create-template-dialog';
import {
  TemplateListDocument,
  type TemplateList__Template as Template,
} from '../queries/list.generated';

const CATEGORIES_SCHEMA = z.array(z.string());

export const TemplateList = () => {
  const { t } = useTranslation();

  const { currentNetwork } = useAppContext();
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const selectedCategories = useMemo(
    () =>
      parseUrlParam(
        () => CATEGORIES_SCHEMA.parse(JSON.parse(searchParams.get('categories') || '[]')),
        'categories',
      ),
    [searchParams],
  );

  const [filterPanelOpen, setFilterPanelOpen] = useState(!isSmDown());
  const [categoryFilterExpanded, setCategoryFilterExpanded] = useState(true);

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [confirmCleanup, confirmCleanupProps] = useConfirmDialog();

  const [deleteTemplate] = useDeleteTemplate();

  const [cleanup] = useCleanupTemplateVersions();

  const [moreMenuProps, moreActionProps, moreTableActionProps] = useMoreActions<Template>();

  const { data, loading, error } = useQuery(TemplateListDocument, { variables: { search } });

  const allCategories = useMemo(() => data?.templateCategories ?? [], [data?.templateCategories]);

  const categoryItems = useMemo(
    () =>
      allCategories
        .filter((category) => category.templates.length > 0)
        .map((category) => ({
          label: category.name,
          selected: selectedCategories.includes(category.name),
          value: category.name,
          count: category.templates.length,
        })),
    [allCategories, selectedCategories],
  );

  const visibleCategories = allCategories.filter(
    (category) =>
      category.templates.length > 0 &&
      (selectedCategories.length === 0 || selectedCategories.includes(category.name)),
  );

  const templateCount = visibleCategories.reduce(
    (count, { templates }) => count + templates.length,
    0,
  );

  const filtersActive = selectedCategories.length > 0;

  const setSelectedCategories = (names: string[]) =>
    setSearchParams((params) => {
      names.length > 0
        ? params.set('categories', JSON.stringify(names))
        : params.delete('categories');
      return params;
    });

  const onCleanup = async () => {
    assert(typeof moreMenuProps.context?.id === 'number', 'cleanup: no template id');
    if (!(await confirmCleanup())) return;
    await cleanup({ variables: { templateIds: moreMenuProps.context.id } });
    moreMenuProps.onClose();
  };

  const onDelete = async () => {
    assert(typeof moreMenuProps.context?.id === 'number', 'delete: no template id');
    if (!(await confirmDelete())) return;
    await deleteTemplate({ variables: { templateId: moreMenuProps.context.id } });
  };
  const [openAddApp, addAppDialogProps] = useCreateTemplateDialog();

  if (error || (!loading && !data?.templateCategories)) return <Navigate replace to="/not-found" />;

  // Nothing to file an upload under, so the dialog would be a dead end — say why rather than
  // letting them pick a zip first.
  const noCategories = !loading && allCategories.length === 0;

  // An App made from a template has to be saved into a content folder, so without one there is
  // nowhere for it to land.
  const canUseTemplates = currentNetwork.canAccessContent.value;

  const NewTemplateButton = (
    <Tooltip title={noCategories ? t('pages.templates.list.new_template_no_categories') : ''}>
      {/* A disabled button fires no events, so the tooltip needs a wrapper to hang on. */}
      <span>
        <Button
          variant="contained"
          color="primary"
          size="medium"
          startIcon={<AddCircle />}
          disabled={noCategories}
          onClick={openAddApp}
        >
          {t('pages.templates.list.new_template_button')}
        </Button>
      </span>
    </Tooltip>
  );

  return (
    <>
      <Helmet title={t('pages.templates.list.title')} />

      <Toolbar
        actions={currentNetwork.canCreateTemplate.value && NewTemplateButton}
        titleIcon={<AppRegistration />}
        titleText={t('pages.templates.list.title')}
      />

      <ListPageContainer>
        <FilterSidebar open={filterPanelOpen} persistAs="templateList:filterWidth">
          <FilterContainer>
            <FilterHeader />
            <FilterAccordionContainer>
              <FilterCollapsibleList
                Icon={Category}
                expanded={categoryFilterExpanded}
                items={categoryItems}
                label={t('pages.templates.list.filters.category')}
                loading={loading && !data}
                onClick={(_, newItems) =>
                  setSelectedCategories(newItems.filter((x) => x.selected).map((x) => x.value))
                }
                onExpand={() => setCategoryFilterExpanded((expanded) => !expanded)}
              />
            </FilterAccordionContainer>
            <FilterFooter handleClear={() => setSelectedCategories([])} />
          </FilterContainer>
        </FilterSidebar>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FilterToggle
                  active={filtersActive}
                  onToggle={() => setFilterPanelOpen((open) => !open)}
                />
                <SearchBar
                  onChange={(value) =>
                    setSearchParams((params) => {
                      value ? params.set('search', value) : params.delete('search');
                      return params;
                    })
                  }
                  placeholder={t('pages.templates.list.search_placeholder')}
                  search={search}
                />
              </Box>
              <Box sx={{ display: { md: 'flex', xs: 'none' }, alignItems: 'center' }}>
                <Count
                  selectedCount={0}
                  totalCount={templateCount}
                  thing="common.pluralize.template_plural"
                />
              </Box>
            </ListActions>
          </ListHeader>
          <ListContent>
            <GridContainer
              loading={loading && !data}
              isEmpty={visibleCategories.length === 0}
              emptyState={
                <EmptyState
                  illustration={AddAppsIllustration}
                  header={t('pages.templates.list.empty_state.header')}
                  description={t('pages.templates.list.empty_state.description')}
                />
              }
            >
              {visibleCategories.map((category) => (
                <Box sx={{ mb: 3 }} key={category.id}>
                  <Typography variant="h4" sx={{ fontWeight: 500, mb: 2 }}>
                    {category.name}
                  </Typography>
                  <Grid container spacing={2}>
                    {category.templates.map((app) => (
                      <Grid item xs={12} sm={6} md={3} lg={2} key={app.id}>
                        <AppCard
                          app={app}
                          onCardTo={app.canUpdate.value ? `${app.id}/settings` : undefined}
                        >
                          {app.latestVersion ? (
                            <Tooltip
                              title={
                                canUseTemplates
                                  ? t('pages.templates.list.use_template_tooltip')
                                  : t('pages.templates.list.use_template_no_content_access')
                              }
                            >
                              {/* A disabled control emits no events, so the tooltip needs a
                                  wrapper to hang on. */}
                              <span>
                                {canUseTemplates ? (
                                  <ButtonLink
                                    sx={{
                                      mb: 1,
                                    }}
                                    size="small"
                                    startIcon={<GetApp />}
                                    to={`${app.id}/create`}
                                    variant="contained"
                                    state={{ returnTo: location.fullPath }}
                                  >
                                    {t('pages.templates.list.use_template_button')}
                                  </ButtonLink>
                                ) : (
                                  // A plain Button rather than a disabled ButtonLink, so there
                                  // is no route left to reach by keyboard.
                                  <Button
                                    sx={{ mb: 1 }}
                                    size="small"
                                    startIcon={<GetApp />}
                                    variant="contained"
                                    disabled
                                  >
                                    {t('pages.templates.list.use_template_button')}
                                  </Button>
                                )}
                              </span>
                            </Tooltip>
                          ) : (
                            <div></div>
                          )}
                          {app.canUpdate.value && (
                            <IconButton
                              label={t('common.buttons.more_actions')}
                              onClick={moreTableActionProps(app).onClick}
                              size="small"
                              color="secondary"
                            >
                              <MoreVert />
                            </IconButton>
                          )}
                        </AppCard>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ))}
            </GridContainer>
          </ListContent>

          <Menu {...moreMenuProps}>
            <MoreActionLink
              Icon={Settings}
              title={t('pages.templates.list.menu.configuration')}
              to={`${moreMenuProps.context?.id}/configuration`}
            />
            <MoreActionLink
              Icon={Edit}
              title={t('common.actions.edit')}
              to={`${moreMenuProps.context?.id}/settings`}
            />
            <MoreActionLink
              Icon={Difference}
              title={t('pages.templates.list.menu.versions')}
              to={`${moreMenuProps.context?.id}/versions`}
            />
            <Divider />
            <MenuItem
              disabled={!moreMenuProps.context?.canCleanup.value}
              onClick={() => void onCleanup()}
            >
              <ListItemIcon>
                <CleaningServices />
              </ListItemIcon>
              <ListItemText>{t('pages.templates.list.menu.delete_unused_versions')}</ListItemText>
            </MenuItem>

            <MoreDeleteAction
              {...moreActionProps}
              disabled={!moreMenuProps.context?.canDestroy.value}
              tooltip={moreMenuProps.context?.canDestroy.reasons?.fullMessages.join(', ') ?? ''}
              onClick={() => void onDelete()}
            />
          </Menu>

          <ConfirmDialog
            {...confirmCleanupProps}
            title={t('pages.templates.list.confirm_cleanup.title')}
            prompt={t('pages.templates.list.confirm_cleanup.prompt')}
            confirm={t('common.dialogs.confirm')}
          />

          <ConfirmDialog
            {...confirmDeleteProps}
            confirm={t('common.dialogs.delete')}
            deleteConfirm
            prompt={t('pages.templates.list.confirm_delete.prompt')}
            title={t('pages.templates.list.confirm_delete.title')}
          />

          <AddAppDialog {...addAppDialogProps} />
        </ListContainer>
      </ListPageContainer>
    </>
  );
};
